import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, Send, Trash2, Pencil, ShieldCheck, Clock } from 'lucide-react';
import CampaignEditor from './CampaignEditor';
import { fmtDate } from '../../lib/utils';

const STATUS_STYLE = {
  draft: 'bg-slate-100 text-slate-600',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-sky-100 text-sky-700',
  scheduled: 'bg-indigo-100 text-indigo-700',
  sending: 'bg-purple-100 text-purple-700',
  sent: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-600',
};

export default function CampaignsTab({ meta }) {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editor, setEditor] = useState(null); // null | {campaign?}
  const [review, setReview] = useState(null); // campaign under review
  const [busy, setBusy] = useState('');

  const load = () => {
    api.get('/comms/campaigns').then((r) => setCampaigns(r.data)).catch(() => {});
    api.get('/comms/templates').then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const del = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/comms/campaigns/${c.id}`); toast.success('Campaign deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="campaigns-tab">
      <div className="flex justify-end">
        <Button className="bg-magenta hover:bg-magenta/90" onClick={() => setEditor({})} data-testid="new-campaign-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Campaign
        </Button>
      </div>

      {campaigns.length === 0 && <p className="text-sm text-slate-400 text-center py-10">No campaigns yet. Create your first campaign.</p>}

      <div className="space-y-2">
        {campaigns.map((c) => (
          <div key={c.id} className="border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3 bg-white" data-testid={`campaign-row-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-navy">{c.name}</span>
                <Badge className={`${STATUS_STYLE[c.status] || ''} border-0 text-[10px] uppercase`} data-testid={`campaign-status-${c.id}`}>{c.status.replace('_', ' ')}</Badge>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {c.subject || 'No subject'} · by {c.created_by_name}
                {c.status === 'scheduled' && c.scheduled_at && <> · <Clock className="w-3 h-3 inline" /> {new Date(c.scheduled_at).toLocaleString()}</>}
                {c.status === 'sent' && <> · sent {fmtDate(c.sent_at)} to {c.recipient_count} · {c.stats?.opened || 0} opens · {c.stats?.clicked || 0} clicks</>}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {!['sending', 'sent'].includes(c.status) && (
                <Button size="sm" variant="outline" onClick={() => setEditor({ campaign: c })} data-testid={`edit-campaign-${c.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              )}
              {meta.is_admin && !['sending', 'sent'].includes(c.status) && (
                <Button size="sm" className="bg-navy hover:bg-navy/90" onClick={() => setReview(c)} data-testid={`review-campaign-${c.id}`}>
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Review & Send
                </Button>
              )}
              {meta.is_admin && (
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => del(c)} data-testid={`delete-campaign-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editor && (
        <CampaignEditor open onClose={() => setEditor(null)} campaign={editor.campaign} templates={templates} meta={meta} onSaved={load} />
      )}
      {review && <ReviewDialog campaign={review} meta={meta} onClose={() => { setReview(null); load(); }} />}
    </div>
  );
}

function ReviewDialog({ campaign, meta, onClose }) {
  const [preview, setPreview] = useState(null);
  const [c, setC] = useState(campaign);
  const [busy, setBusy] = useState('');
  const [testTo, setTestTo] = useState('');
  const [when, setWhen] = useState('');

  useEffect(() => {
    api.post('/comms/recipients-preview', { audience: c.audience || {} }).then((r) => setPreview(r.data)).catch(() => {});
  }, []);

  const approve = async () => {
    setBusy('approve');
    try { const r = await api.post(`/comms/campaigns/${c.id}/approve`); setC(r.data); toast.success('Campaign approved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); } finally { setBusy(''); }
  };
  const send = async (mode) => {
    if (mode === 'now' && !window.confirm(`Send "${c.name}" to ${preview?.count ?? '?'} recipients NOW?`)) return;
    setBusy(mode);
    try {
      await api.post(`/comms/campaigns/${c.id}/send`, mode === 'now' ? { mode: 'now' } : { mode: 'schedule', scheduled_at: new Date(when).toISOString() });
      toast.success(mode === 'now' ? 'Campaign is sending' : 'Campaign scheduled');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); } finally { setBusy(''); }
  };
  const sendTest = async () => {
    setBusy('test');
    try { const r = await api.post(`/comms/campaigns/${c.id}/send-test`, { to: testTo || undefined }); toast.success(`Test sent to ${r.data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); } finally { setBusy(''); }
  };

  const rows = [
    ['Campaign', c.name],
    ['Subject', c.subject || '—'],
    ['Recipients', preview ? `${preview.count} contacts (${preview.crm_matched} from CRM)` : '…'],
    ['Sender', meta.sender],
    ['Reply-To', meta.reply_to],
    ['Scheduled', c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : 'Not scheduled'],
    ['Status', c.status.replace('_', ' ')],
  ];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="review-dialog">
        <DialogHeader><DialogTitle>Review & Approve Campaign</DialogTitle></DialogHeader>
        <div className="divide-y text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between py-2 gap-4">
              <span className="text-slate-500">{k}</span>
              <span className="font-medium text-navy text-right" data-testid={`review-${k.toLowerCase()}`}>{v}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Input className="h-9 text-sm" placeholder="Send test to… (default: you)" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="review-test-input" />
          <Button variant="outline" size="sm" onClick={sendTest} disabled={!!busy} data-testid="review-send-test">
            {busy === 'test' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Test'}
          </Button>
        </div>
        {c.status !== 'approved' && c.status !== 'scheduled' ? (
          <Button className="bg-navy hover:bg-navy/90 w-full" onClick={approve} disabled={!!busy} data-testid="approve-campaign-btn">
            {busy === 'approve' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />} Approve Campaign
          </Button>
        ) : (
          <div className="space-y-2">
            <Button className="bg-magenta hover:bg-magenta/90 w-full" onClick={() => send('now')} disabled={!!busy} data-testid="send-now-btn">
              {busy === 'now' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send Now to {preview?.count ?? '…'} recipients
            </Button>
            <div className="flex gap-1.5">
              <Input type="datetime-local" className="h-9 text-sm" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="schedule-datetime" />
              <Button variant="outline" onClick={() => send('schedule')} disabled={!when || !!busy} data-testid="schedule-btn">
                {busy === 'schedule' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Schedule'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
